// Dashboard click-through (collections sprint, Instance D): a chart bar opens the claims
// ledger with that bar's filters applied.
//
// Atlas has no router and the Dashboard is not handed `onPageChange` (App.js renders it with
// the practice alone), so the hand-off is two parts, neither of them new state management:
//
//   1. the filters travel in one module-scoped variable the ledger reads ONCE on mount, the
//      same shape as `features/claims/claimEditorTarget.js`, keyed by practice so it can
//      never apply to another practice's ledger;
//   2. the navigation is the one App.js already honours from the browser's Back button:
//      push `/p/<practice>/claims` and announce it with `popstate`, and App's own listener
//      sets the page from the URL. Back returns to the dashboard.
//
// Nothing is stored in sessionStorage: filters can carry a payer name, and a lost hand-off
// on a hard refresh costs one click.

import { statusGroups } from "../../utils/claimStatus";

let pending = null; // { practiceId, filters }

// A Claims Status segment as the ledger's Status checkboxes, so the click lands on the
// filter a person would use rather than a separate chip (David, 2026-10-21). The dashboard
// buckets (routes/ledger.py STATUS_CATEGORIES) are the client view's status groups, which
// fold Accepted into Submitted for every role.
// Not carried: the server also counts a blank or unmapped status as `draft`, which no
// checkbox can select, so such rows (rare) show on the bar but not in the ledger.

/** The Status filter value (comma-separated) for a dashboard category; "" for none. */
export function statusesForCategory(category) {
  const group = statusGroups(true).find((g) => g.key === category);
  return group ? group.statuses.join(",") : "";
}

/** Month ("2026-07") to its first and last day, clipped to the dashboard's own range. */
export function monthRange(month, rangeStart, rangeEnd) {
  const [year, mon] = String(month).split("-").map(Number);
  if (!year || !mon) return { date_from: rangeStart || "", date_to: rangeEnd || "" };
  const first = `${year}-${String(mon).padStart(2, "0")}-01`;
  const lastDay = new Date(year, mon, 0).getDate();
  const last = `${year}-${String(mon).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
  return {
    date_from: rangeStart && rangeStart > first ? rangeStart : first,
    date_to: rangeEnd && rangeEnd < last ? rangeEnd : last,
  };
}

export function openLedgerWith(practiceId, filters) {
  if (!practiceId) return;
  pending = { practiceId: Number(practiceId), filters: { ...filters } };
  const path = `/p/${practiceId}/claims`;
  try {
    window.history.pushState({}, "", path);
    window.dispatchEvent(new PopStateEvent("popstate"));
  } catch (_) {
    /* a browser without the History API: the filters wait for the next ledger visit */
  }
}

/** The filters handed to this practice's ledger, or null. Read in the ledger's state
    initialiser; `clearLedgerLink` runs from its mount effect. Two calls rather than one
    "take", because React may call an initialiser twice and the second must still see it. */
export function peekLedgerLink(practiceId) {
  if (!pending || pending.practiceId !== Number(practiceId)) return null;
  return pending.filters;
}

export function clearLedgerLink() {
  pending = null;
}
